import * as z from 'zod';

const currentAssetKeys = [
    "cash",
    "restrictedCash",
    "marketableSecurities",
    "accountsReceivable",
    "contractAssets",
    "retainage",
    "underbillings",
    "inventory",
    "prepaids",
    "currentShareholderEmplReceivables",
    "relatedPartyReceivables",
    "otherCurrentAssets",
];
const fixedAssetKeys = [
    "generalFixedAssets",
    "realEstate",
    "equipment",
    "officeEquipmentFurnitureFixtures",
    "software",
    "accumulatedDepreciation",
    "otherFixedAssets",
];
const nonCurrentAssetKeys = [
    "otherReceivables",
    "relatedPartyReceivables",
    "nonCurrentShareholderEmplReceivables",
    "deposits",
    "rouAssets",
    "goodwill",
    "intangibles",
    "investment",
    "otherNonCurrentAssets",
    "currentAssetsDropped",
];
const currentLiabilityKeys = [
    "accountsPayable",
    "contractLiabilities",
    "retainagePayable",
    "overbillings",
    "customerDeposits",
    "accruedExpenses",
    "accruedLossesOnContracts",
    "creditCardsBalance",
    "cashOverdraft",
    "blocBalance",
    "currentPortionOfLtd",
    "otherNotesPayable",
    "taxesPayable",
    "taxesDeferred",
    "relatedPartyPayable",
    "shareholderEmplPayable",
    "currentPortionOfRouLiabilities",
    "otherCurrentLiabilities",
];
const nonCurrentLiabilityKeys = [
    "ltdBankTermLoan",
    "ltdRealEstate",
    "ltdEquipment",
    "ltdOther",
    "ltdShareholder",
    "ltdRelatedParty",
    "taxesDeferred",
    "ltPortionOfRouLiabilities",
    "ltPortionOfAccruedLossesOnContracts",
    "otherLtLiabilities",
    "currentLiabilitiesDropped",
];
const equityKeys = [
    "stocksIssuedAndOutstanding",
    "treasuryStock",
    "paidInCapital",
    "retainedEarnings",
    "currentYearEarnings",
    "distributions",
    "otherEquity",
    "equityAdjustments",
];
const defineBalanceSheetLineItemPaths = (paths) => paths;
const balanceSheetLineItemPaths = defineBalanceSheetLineItemPaths(Object.freeze([
    "assets.current.cash",
    "assets.current.restrictedCash",
    "assets.current.marketableSecurities",
    "assets.current.accountsReceivable",
    "assets.current.contractAssets",
    "assets.current.retainage",
    "assets.current.underbillings",
    "assets.current.inventory",
    "assets.current.prepaids",
    "assets.current.currentShareholderEmplReceivables",
    "assets.current.relatedPartyReceivables",
    "assets.current.otherCurrentAssets",
    "assets.fixed.generalFixedAssets",
    "assets.fixed.realEstate",
    "assets.fixed.equipment",
    "assets.fixed.officeEquipmentFurnitureFixtures",
    "assets.fixed.software",
    "assets.fixed.accumulatedDepreciation",
    "assets.fixed.otherFixedAssets",
    "assets.nonCurrent.otherReceivables",
    "assets.nonCurrent.relatedPartyReceivables",
    "assets.nonCurrent.nonCurrentShareholderEmplReceivables",
    "assets.nonCurrent.deposits",
    "assets.nonCurrent.rouAssets",
    "assets.nonCurrent.goodwill",
    "assets.nonCurrent.intangibles",
    "assets.nonCurrent.investment",
    "assets.nonCurrent.otherNonCurrentAssets",
    "assets.nonCurrent.currentAssetsDropped",
    "liabilities.current.accountsPayable",
    "liabilities.current.contractLiabilities",
    "liabilities.current.retainagePayable",
    "liabilities.current.overbillings",
    "liabilities.current.customerDeposits",
    "liabilities.current.accruedExpenses",
    "liabilities.current.accruedLossesOnContracts",
    "liabilities.current.creditCardsBalance",
    "liabilities.current.cashOverdraft",
    "liabilities.current.blocBalance",
    "liabilities.current.currentPortionOfLtd",
    "liabilities.current.otherNotesPayable",
    "liabilities.current.taxesPayable",
    "liabilities.current.taxesDeferred",
    "liabilities.current.relatedPartyPayable",
    "liabilities.current.shareholderEmplPayable",
    "liabilities.current.currentPortionOfRouLiabilities",
    "liabilities.current.otherCurrentLiabilities",
    "liabilities.nonCurrent.ltdBankTermLoan",
    "liabilities.nonCurrent.ltdRealEstate",
    "liabilities.nonCurrent.ltdEquipment",
    "liabilities.nonCurrent.ltdOther",
    "liabilities.nonCurrent.ltdShareholder",
    "liabilities.nonCurrent.ltdRelatedParty",
    "liabilities.nonCurrent.taxesDeferred",
    "liabilities.nonCurrent.ltPortionOfRouLiabilities",
    "liabilities.nonCurrent.ltPortionOfAccruedLossesOnContracts",
    "liabilities.nonCurrent.otherLtLiabilities",
    "liabilities.nonCurrent.currentLiabilitiesDropped",
    "equity.stocksIssuedAndOutstanding",
    "equity.treasuryStock",
    "equity.paidInCapital",
    "equity.retainedEarnings",
    "equity.currentYearEarnings",
    "equity.distributions",
    "equity.otherEquity",
    "equity.equityAdjustments",
]));
const balanceSheetGroupLabels = {
    "assets.current.cash": "Cash",
    "assets.current.restrictedCash": "Restricted Cash",
    "assets.current.marketableSecurities": "Marketable Securities",
    "assets.current.accountsReceivable": "Accounts Receivable",
    "assets.current.contractAssets": "Contract Assets",
    "assets.current.retainage": "Retainage",
    "assets.current.underbillings": "Underbillings",
    "assets.current.inventory": "Inventory",
    "assets.current.prepaids": "Prepaids",
    "assets.current.currentShareholderEmplReceivables": "Current Shareholder/Employee Receivables",
    "assets.current.relatedPartyReceivables": "Related Party Receivables",
    "assets.current.otherCurrentAssets": "Other Current Assets",
    "assets.fixed.generalFixedAssets": "General Fixed Assets",
    "assets.fixed.realEstate": "Real Estate",
    "assets.fixed.equipment": "Equipment",
    "assets.fixed.officeEquipmentFurnitureFixtures": "Office Equipment, Furniture & Fixtures",
    "assets.fixed.software": "Software",
    "assets.fixed.accumulatedDepreciation": "Accumulated Depreciation",
    "assets.fixed.otherFixedAssets": "Other Fixed Assets",
    "assets.nonCurrent.otherReceivables": "Other Receivables",
    "assets.nonCurrent.relatedPartyReceivables": "Related Party Receivables",
    "assets.nonCurrent.nonCurrentShareholderEmplReceivables": "Non-Current Shareholder/Employee Receivables",
    "assets.nonCurrent.deposits": "Deposits",
    "assets.nonCurrent.rouAssets": "ROU Assets",
    "assets.nonCurrent.goodwill": "Goodwill",
    "assets.nonCurrent.intangibles": "Intangibles",
    "assets.nonCurrent.investment": "Investment",
    "assets.nonCurrent.otherNonCurrentAssets": "Other Non-Current Assets",
    "assets.nonCurrent.currentAssetsDropped": "Current Assets Dropped",
    "liabilities.current.accountsPayable": "Accounts Payable",
    "liabilities.current.contractLiabilities": "Contract Liabilities",
    "liabilities.current.retainagePayable": "Retainage Payable",
    "liabilities.current.overbillings": "Overbillings",
    "liabilities.current.customerDeposits": "Customer Deposits",
    "liabilities.current.accruedExpenses": "Accrued Expenses",
    "liabilities.current.accruedLossesOnContracts": "Accrued Losses on Contracts",
    "liabilities.current.creditCardsBalance": "Credit Cards Balance",
    "liabilities.current.cashOverdraft": "Cash Overdraft",
    "liabilities.current.blocBalance": "BLOC Balance",
    "liabilities.current.currentPortionOfLtd": "Current Portion of LTD",
    "liabilities.current.otherNotesPayable": "Other Notes Payable",
    "liabilities.current.taxesPayable": "Taxes Payable",
    "liabilities.current.taxesDeferred": "Taxes Deferred",
    "liabilities.current.relatedPartyPayable": "Related Party Payable",
    "liabilities.current.shareholderEmplPayable": "Shareholder/Employee Payable",
    "liabilities.current.currentPortionOfRouLiabilities": "Current Portion of ROU Liabilities",
    "liabilities.current.otherCurrentLiabilities": "Other Current Liabilities",
    "liabilities.nonCurrent.ltdBankTermLoan": "LTD Bank Term Loan",
    "liabilities.nonCurrent.ltdRealEstate": "LTD Real Estate",
    "liabilities.nonCurrent.ltdEquipment": "LTD Equipment",
    "liabilities.nonCurrent.ltdOther": "LTD Other",
    "liabilities.nonCurrent.ltdShareholder": "LTD Shareholder",
    "liabilities.nonCurrent.ltdRelatedParty": "LTD Related Party",
    "liabilities.nonCurrent.taxesDeferred": "Taxes Deferred",
    "liabilities.nonCurrent.ltPortionOfRouLiabilities": "LT Portion of ROU Liabilities",
    "liabilities.nonCurrent.ltPortionOfAccruedLossesOnContracts": "LT Portion of Accrued Losses on Contracts",
    "liabilities.nonCurrent.otherLtLiabilities": "Other LT Liabilities",
    "liabilities.nonCurrent.currentLiabilitiesDropped": "Current Liabilities Dropped",
    "equity.stocksIssuedAndOutstanding": "Stocks Issued and Outstanding",
    "equity.treasuryStock": "Treasury Stock",
    "equity.paidInCapital": "Paid-In Capital",
    "equity.retainedEarnings": "Retained Earnings",
    "equity.currentYearEarnings": "Current Year Earnings",
    "equity.distributions": "Distributions",
    "equity.otherEquity": "Other Equity",
    "equity.equityAdjustments": "Adjustments",
};
const balanceSheetLineItemPathSet = new Set(balanceSheetLineItemPaths);
const isBalanceSheetLineItemPath = (value) => typeof value === "string" && balanceSheetLineItemPathSet.has(value);
var BalanceSheetLineItemPath;
(function (BalanceSheetLineItemPath) {
    BalanceSheetLineItemPath.schema = z.custom(isBalanceSheetLineItemPath, "Invalid balance sheet line item path");
})(BalanceSheetLineItemPath || (BalanceSheetLineItemPath = {}));

export { BalanceSheetLineItemPath, balanceSheetGroupLabels, balanceSheetLineItemPaths, currentAssetKeys, currentLiabilityKeys, equityKeys, fixedAssetKeys, isBalanceSheetLineItemPath, nonCurrentAssetKeys, nonCurrentLiabilityKeys };
